import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { IncomeCategory, TransactionSort, TransactionType } from "@boo/shared";
import type {
  Transaction,
  ExpenseCategory,
  CardIssuer,
  ClassifyIncomingRequest,
} from "@boo/shared";

const INCOME_CATEGORY_VALUES = new Set<string>(Object.values(IncomeCategory));
import { dayjs } from "@boo/shared";
import { EMPTY_TRANSACTION_FILTERS } from "../types";
import type { MonthlySummaryPageData, TransactionFilters } from "../types";
import {
  deleteTransaction as deleteTransactionApi,
  patchTransactionAccount,
  fetchMonthlySummary,
  fetchTransactions,
  patchTransactionCategory,
  patchTransactionCardPayment,
  classifyIncoming as classifyIncomingApi,
  updateIncomeTransaction as updateIncomeTransactionApi,
  type UpdateTransactionPayload,
} from "../../../services/expenseApi";
import { upsertAlias } from "../../../services/merchantAliasApi";
import { fetchPendingCount } from "../../../services/reconciliationApi";
import { mapSummaryToPageData } from "../lib/mappers";
import { useRecurringIncomeStore } from "./useRecurringIncomeStore";

type SummaryData = Omit<
  MonthlySummaryPageData,
  "transactions" | "recurringItems" | "insights"
>;

interface AppState {
  viewYear: number;
  viewMonth: number; // 1-based
  summary: SummaryData | null;
  summaryLoading: boolean;
  summaryError: string | null;
  transactions: Transaction[];
  transactionsPage: number;
  transactionsTotal: number;
  transactionsLoading: boolean;
  transactionsError: string | null;
  transactionSort: TransactionSort;
  transactionFilters: TransactionFilters;
  pendingReconciliationCount: number;
  loadPendingReconciliationCount: () => Promise<void>;
  loadMonth: (year: number, month: number) => Promise<void>;
  goToPrevMonth: () => void;
  goToNextMonth: () => void;
  loadMoreTransactions: () => Promise<void>;
  reloadTransactions: () => Promise<void>;
  setTransactionSort: (sort: TransactionSort) => Promise<void>;
  setTransactionFilters: (patch: Partial<TransactionFilters>) => Promise<void>;
  clearTransactionFilters: () => Promise<void>;
  categorizeTransaction: (
    id: string,
    category: ExpenseCategory | IncomeCategory,
  ) => Promise<void>;
  setCardPayment: (id: string, issuer: CardIssuer | null) => Promise<void>;
  classifyIncoming: (
    id: string,
    payload: ClassifyIncomingRequest,
  ) => Promise<void>;
  deleteTransaction: (id: string) => Promise<void>;
  setTransactionAccount: (
    id: string,
    accountId: string | null,
  ) => Promise<void>;
  updateIncomeTransaction: (
    id: string,
    payload: UpdateTransactionPayload,
  ) => Promise<void>;
  renamePayee: (
    rawName: string,
    alias: string,
  ) => Promise<{ matchedCount: number }>;
}

const now = dayjs();

// Bumped on every list fetch so a slow response can't overwrite a newer one.
let txRequestSeq = 0;

export const useAppStore = create<AppState>((set, get) => ({
  viewYear: now.year(),
  viewMonth: now.month() + 1,
  summary: null,
  summaryLoading: false,
  summaryError: null,
  transactions: [],
  transactionsPage: 1,
  transactionsTotal: 0,
  transactionsLoading: false,
  transactionsError: null,
  transactionSort: TransactionSort.DateDesc,
  transactionFilters: EMPTY_TRANSACTION_FILTERS,
  pendingReconciliationCount: 0,

  async loadPendingReconciliationCount() {
    try {
      set({ pendingReconciliationCount: await fetchPendingCount() });
    } catch (err) {
      // Non-fatal — the indicator simply stays hidden if the count can't load.
      console.error("Failed to load pending reconciliation count:", err);
    }
  },

  async loadMonth(year, month) {
    set({
      viewYear: year,
      viewMonth: month,
      summaryLoading: true,
      summaryError: null,
      transactionsLoading: true,
      transactionsError: null,
      transactions: [],
      transactionsPage: 1,
      transactionsTotal: 0,
    });

    try {
      await useRecurringIncomeStore.getState().applyForPeriod(year, month);
    } catch {
      // non-fatal — still render whatever is in the DB
    }

    const seq = ++txRequestSeq;
    const { transactionSort, transactionFilters } = get();
    const [summaryResult, txResult] = await Promise.allSettled([
      fetchMonthlySummary(year, month),
      fetchTransactions({
        year,
        month,
        page: 1,
        sort: transactionSort,
        ...transactionFilters,
      }),
    ]);

    if (summaryResult.status === "fulfilled") {
      set({
        summary: mapSummaryToPageData(summaryResult.value, year, month),
        summaryLoading: false,
      });
    } else {
      set({
        summaryError:
          (summaryResult.reason as Error)?.message ?? "Failed to load summary",
        summaryLoading: false,
      });
    }

    if (seq !== txRequestSeq) return;
    if (txResult.status === "fulfilled") {
      set({
        transactions: txResult.value.data,
        transactionsPage: 1,
        transactionsTotal: txResult.value.total,
        transactionsLoading: false,
      });
    } else {
      set({
        transactionsError:
          (txResult.reason as Error)?.message ?? "Failed to load transactions",
        transactionsLoading: false,
      });
    }
  },

  goToPrevMonth() {
    const { viewYear, viewMonth } = get();
    const prev = dayjs(
      `${viewYear}-${String(viewMonth).padStart(2, "0")}-01`,
    ).subtract(1, "month");
    get().loadMonth(prev.year(), prev.month() + 1);
  },

  goToNextMonth() {
    const { viewYear, viewMonth } = get();
    const next = dayjs(
      `${viewYear}-${String(viewMonth).padStart(2, "0")}-01`,
    ).add(1, "month");
    get().loadMonth(next.year(), next.month() + 1);
  },

  async loadMoreTransactions() {
    const {
      viewYear,
      viewMonth,
      transactionsPage,
      transactionsTotal,
      transactions,
      transactionsLoading,
      transactionSort,
      transactionFilters,
    } = get();
    if (transactionsLoading || transactions.length >= transactionsTotal) return;

    const nextPage = transactionsPage + 1;
    const seq = ++txRequestSeq;
    set({ transactionsLoading: true, transactionsError: null });

    try {
      const result = await fetchTransactions({
        year: viewYear,
        month: viewMonth,
        page: nextPage,
        sort: transactionSort,
        ...transactionFilters,
      });
      if (seq !== txRequestSeq) return;
      set({
        transactions: [...transactions, ...result.data],
        transactionsPage: nextPage,
        transactionsTotal: result.total,
        transactionsLoading: false,
      });
    } catch (err: unknown) {
      if (seq !== txRequestSeq) return;
      set({
        transactionsError:
          err instanceof Error ? err.message : "Failed to load more",
        transactionsLoading: false,
      });
    }
  },

  async reloadTransactions() {
    const { viewYear, viewMonth, transactionSort, transactionFilters } = get();
    const seq = ++txRequestSeq;
    set({ transactionsLoading: true, transactionsError: null });

    try {
      const result = await fetchTransactions({
        year: viewYear,
        month: viewMonth,
        page: 1,
        sort: transactionSort,
        ...transactionFilters,
      });
      if (seq !== txRequestSeq) return;
      set({
        transactions: result.data,
        transactionsPage: 1,
        transactionsTotal: result.total,
        transactionsLoading: false,
      });
    } catch (err: unknown) {
      if (seq !== txRequestSeq) return;
      set({
        transactionsError:
          err instanceof Error ? err.message : "Failed to load transactions",
        transactionsLoading: false,
      });
    }
  },

  setTransactionSort(sort) {
    set({ transactionSort: sort });
    return get().reloadTransactions();
  },

  setTransactionFilters(patch) {
    const next = { ...get().transactionFilters, ...patch };
    // Category filter only covers expense categories.
    if (
      next.transactionType &&
      next.transactionType !== TransactionType.EXPENSE
    )
      next.category = null;
    set({ transactionFilters: next });
    return get().reloadTransactions();
  },

  clearTransactionFilters() {
    set({
      transactionSort: TransactionSort.DateDesc,
      transactionFilters: EMPTY_TRANSACTION_FILTERS,
    });
    return get().reloadTransactions();
  },

  async categorizeTransaction(id, category) {
    const prev = get().transactions;
    // Optimistic update — income transactions only set incomeCategory, but we
    // also null out category so the needs-a-category filter (category === null)
    // removes them.
    const patch = INCOME_CATEGORY_VALUES.has(category)
      ? {
          incomeCategory: category as IncomeCategory,
          category: null,
        }
      : { category: category as ExpenseCategory };
    set({
      transactions: prev.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    });
    try {
      await patchTransactionCategory(id, category);
      const { viewYear, viewMonth } = get();
      const updated = await fetchMonthlySummary(viewYear, viewMonth);
      set({ summary: mapSummaryToPageData(updated, viewYear, viewMonth) });
    } catch (err) {
      console.error("Failed to update category:", err);
      // Revert on failure
      set({ transactions: prev });
    }
  },

  async setCardPayment(id, issuer) {
    try {
      const updated = await patchTransactionCardPayment(id, issuer);
      set({
        transactions: get().transactions.map((t) =>
          t.id === id ? { ...t, ...updated } : t,
        ),
      });
      const { viewYear, viewMonth } = get();
      const summary = await fetchMonthlySummary(viewYear, viewMonth);
      set({ summary: mapSummaryToPageData(summary, viewYear, viewMonth) });
    } catch (err) {
      console.error("Failed to update card payment:", err);
    }
  },

  // Classifying money received can change other rows too (the expense it
  // repays, other deposits from a remembered sender), so reload the month.
  // Errors propagate to the caller, which shows them next to the row.
  async classifyIncoming(id, payload) {
    await classifyIncomingApi(id, payload);
    const { viewYear, viewMonth } = get();
    const [summary] = await Promise.all([
      fetchMonthlySummary(viewYear, viewMonth),
      get().reloadTransactions(),
      useRecurringIncomeStore.getState().loadItems(),
    ]);
    set({ summary: mapSummaryToPageData(summary, viewYear, viewMonth) });
  },

  async deleteTransaction(id) {
    const prev = get().transactions;
    const prevTotal = get().transactionsTotal;
    set({
      transactions: prev.filter((t) => t.id !== id),
      transactionsTotal: prevTotal - 1,
    });
    try {
      await deleteTransactionApi(id);
      const { viewYear, viewMonth } = get();
      const updated = await fetchMonthlySummary(viewYear, viewMonth);
      set({ summary: mapSummaryToPageData(updated, viewYear, viewMonth) });
    } catch (err) {
      console.error("Failed to delete transaction:", err);
      set({ transactions: prev, transactionsTotal: prevTotal });
    }
  },

  async setTransactionAccount(id, accountId) {
    const prev = get().transactions;
    const prevTotal = get().transactionsTotal;
    // Under the Unassigned filter an assigned row no longer belongs in the list.
    const leavesList =
      get().transactionFilters.unassigned && accountId !== null;
    set({
      transactions: leavesList
        ? prev.filter((t) => t.id !== id)
        : prev.map((t) => (t.id === id ? { ...t, accountId } : t)),
      transactionsTotal: leavesList ? prevTotal - 1 : prevTotal,
    });
    try {
      await patchTransactionAccount(id, accountId);
    } catch (err) {
      console.error("Failed to change transaction account:", err);
      set({ transactions: prev, transactionsTotal: prevTotal });
      throw err;
    }
  },

  async updateIncomeTransaction(id, payload) {
    const prev = get().transactions;
    set({
      transactions: prev.map((t) => (t.id === id ? { ...t, ...payload } : t)),
    });
    try {
      const updated = await updateIncomeTransactionApi(id, payload);
      set({
        transactions: get().transactions.map((t) =>
          t.id === id ? updated : t,
        ),
      });
      const { viewYear, viewMonth } = get();
      const summary = await fetchMonthlySummary(viewYear, viewMonth);
      set({ summary: mapSummaryToPageData(summary, viewYear, viewMonth) });
    } catch (err) {
      console.error("Failed to update income transaction:", err);
      set({ transactions: prev });
    }
  },
  async renamePayee(rawName, alias) {
    const result = await upsertAlias(rawName, alias);
    set({
      transactions: get().transactions.map((t) =>
        t.title === rawName ? { ...t, displayTitle: alias } : t,
      ),
    });
    return { matchedCount: result.matchedCount };
  },
}));

export const useCurrentMonth = () =>
  useAppStore(
    useShallow((state) =>
      state.summary
        ? {
            ...state.summary,
            transactions: state.transactions,
          }
        : null,
    ),
  );
